import { useCallback, useEffect, useRef } from "react";
import { CustomField } from "models/interfaces";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  removeCustomField,
  setCustomFields,
  upsertCustomField,
} from "redux/appSlice";
import {
  createCustomField,
  deleteCustomField,
  fetchCustomFields,
  updateCustomField,
} from "../SignRequestService";
import { handleErrorMessage } from "helpers/methods";
import { isEmbedEditorMode } from "embed/EmbedEditorMode";

/**
 * The sender's custom field library for the editor's left panel. Loads once
 * per editor session into redux (both the desktop and the mobile panel read
 * the same list) and exposes the create / update / delete calls, keeping the
 * store in step with the server.
 */
const EMPTY_LIBRARY: CustomField[] = [];

const useCustomFieldLibrary = () => {
  const dispatch = useAppDispatch();
  // The app slice is rehydrated from localStorage; a blob written before this
  // key existed has no `customFields`, so never assume the array is there.
  const customFields: CustomField[] = useAppSelector(
    (state) => state.app.customFields ?? EMPTY_LIBRARY,
  );
  const loadedRef = useRef(false);
  // The embedded editor (partner iframe) gets the library in its load bundle and
  // has no user session to create, edit or delete definitions with.
  const isEmbedEditor = isEmbedEditorMode();

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    if (isEmbedEditor) return;
    let cancelled = false;
    (async () => {
      try {
        const fields = await fetchCustomFields();
        if (!cancelled) dispatch(setCustomFields(fields));
      } catch (error: any) {
        // An empty library is the common case; a real failure still reaches
        // the console without blocking the editor.
        console.error("Failed to load custom fields", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, isEmbedEditor]);

  const save = useCallback(
    async (customField: CustomField): Promise<boolean> => {
      if (isEmbedEditor) return false;
      try {
        if (customField.id) {
          await updateCustomField(customField.id, customField);
          dispatch(upsertCustomField(customField));
        } else {
          const id = await createCustomField(customField);
          dispatch(upsertCustomField({ ...customField, id: String(id) }));
        }
        return true;
      } catch (error: any) {
        handleErrorMessage(error);
        return false;
      }
    },
    [dispatch],
  );

  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      if (isEmbedEditor) return false;
      try {
        await deleteCustomField(id);
        dispatch(removeCustomField(id));
        return true;
      } catch (error: any) {
        handleErrorMessage(error);
        return false;
      }
    },
    [dispatch],
  );

  return { customFields, save, remove, canManage: !isEmbedEditor };
};

export default useCustomFieldLibrary;
